import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import PropTypes from 'prop-types';
import BottomNavigation from '@material-ui/core/BottomNavigation';
import BottomNavigationAction from '@material-ui/core/BottomNavigationAction';
import {
  MenuDnaIcon,
  MenuRecommendationsIcon,
  MenuSeverityIcon,
  MenuSummaryIcon,
  MenuLikelihoodIcon,
} from 'on_web/assets/iconsList';

import { useTranslation } from 'react-i18next';
import { APP_PRIMARY, NEVADA_GRAY } from 'colours';
import {
  ROUTE_ASSESSMENT_DNA_RISKS,
  ROUTE_ASSESSMENT_ENTERPRISE_DEMO,
  ROUTE_ASSESSMENT_RECOMMENDATIONS,
  ROUTE_ASSESSMENT_HEALTH_RISKS,
  ROUTE_ASSESSMENT_HOME,
  ROUTE_ASSESSMENT_LIKELIHOOD,
} from 'routes';

import useCheckScreenSize from 'on_web/hooks/useCheckScreenSize';

import { reportsBottomNavigationOptions } from 'on_web/helpers/redirectHelper';
import { trackEvent } from 'on_web/helpers/userTrackingEventsHelper';

/**
 * Component displaying navigation at the bottom of the Reports section.
 *
 * Component knows to handle only predefined navigation options and it is
 * not possible to pass N navigation options which need to be rendered by
 * this component. This limitation exists because of icons that are used
 * for each navigation option.
 * @param {Component properties} props
 */
export default function ReportsBottomNavigation(props) {
  const useStyles = makeStyles({
    // this will make the component sticky
    root: {
      width: '100%',
      position: 'fixed',
      bottom: 0,
      zIndex: 9999,
    },
    actionRoot: {
      maxWidth:
        process.env.REACT_APP_HIDE_DNA_SECTION === 'true' ? '250px' : '168px',
      border: '0px',
      padding: '6px 2px 8px',
      minWidth: 'auto',
    },
  });
  const classes = useStyles();
  const [value, setValue] = React.useState(props.selected);

  const [isDesktop] = useCheckScreenSize();

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (props.onSelected) {
      let redirectionRoute = '';
      let redirectOption = '';
      if (newValue === reportsBottomNavigationOptions.home) {
        redirectionRoute = ROUTE_ASSESSMENT_HOME;
        redirectOption = 'home';
      } else if (newValue === reportsBottomNavigationOptions.healthRisk) {
        redirectionRoute = ROUTE_ASSESSMENT_HEALTH_RISKS;
        redirectOption = 'health-risks';
      } else if (newValue === reportsBottomNavigationOptions.dnaRisk) {
        redirectionRoute = ROUTE_ASSESSMENT_DNA_RISKS;
        redirectOption = 'dna-risks';
      } else if (newValue === reportsBottomNavigationOptions.recommendations) {
        redirectionRoute = ROUTE_ASSESSMENT_RECOMMENDATIONS;
        redirectOption = 'recommendations';
      } else if (newValue === reportsBottomNavigationOptions.likelihood) {
        redirectionRoute = ROUTE_ASSESSMENT_LIKELIHOOD;
        redirectOption = 'likelihood-history';
      }

      trackEvent('Reporting', `Navigate mobile footer menu ${redirectOption}`);
      props.onSelected(redirectionRoute);
    }
  };

  const { t } = useTranslation();

  const getLabel = text => {
    return <span style={{ fontSize: '0.6rem' }}>{text}</span>;
  };

  return (
    <>
      {isDesktop ? (
        <React.Fragment />
      ) : (
        <Box mt={props.marginTop}>
          <BottomNavigation
            value={value}
            onChange={handleChange}
            className={classes.root}
            showLabels
            style={{ borderTop: '1px Solid #efefef' }}
          >
            <BottomNavigationAction
              className={classes.actionRoot}
              label={getLabel(t('reporting.common.navigation.home'))}
              value={reportsBottomNavigationOptions.home}
              icon={
                <MenuSummaryIcon
                  color={
                    value === reportsBottomNavigationOptions.home
                      ? APP_PRIMARY
                      : NEVADA_GRAY
                  }
                  width="24px"
                  height="24px"
                />
              }
            />
            <BottomNavigationAction
              className={classes.actionRoot}
              label={getLabel(t('reporting.common.navigation.healthRisk'))}
              value={reportsBottomNavigationOptions.healthRisk}
              icon={
                <MenuSeverityIcon
                  color={
                    value === reportsBottomNavigationOptions.healthRisk
                      ? APP_PRIMARY
                      : NEVADA_GRAY
                  }
                  width="24px"
                  height="24px"
                />
              }
            />
            <BottomNavigationAction
              className={classes.actionRoot}
              label={getLabel(t('reporting.common.navigation.dna'))}
              value={reportsBottomNavigationOptions.dnaRisk}
              icon={
                <MenuDnaIcon
                  color={
                    value === reportsBottomNavigationOptions.dnaRisk
                      ? APP_PRIMARY
                      : NEVADA_GRAY
                  }
                  width="24px"
                  height="24px"
                />
              }
            />
            <BottomNavigationAction
              className={classes.actionRoot}
              label={getLabel(t('reporting.common.navigation.history'))}
              value={reportsBottomNavigationOptions.likelihood}
              icon={
                <MenuLikelihoodIcon
                  color={
                    value === reportsBottomNavigationOptions.likelihood
                      ? APP_PRIMARY
                      : NEVADA_GRAY
                  }
                  width="24px"
                  height="24px"
                />
              }
            />

            <BottomNavigationAction
              className={classes.actionRoot}
              label={getLabel(t('reporting.common.navigation.recommendations'))}
              value={reportsBottomNavigationOptions.recommendations}
              icon={
                <MenuRecommendationsIcon
                  color={
                    value === reportsBottomNavigationOptions.recommendations
                      ? APP_PRIMARY
                      : NEVADA_GRAY
                  }
                  width="24px"
                  height="24px"
                />
              }
            />
          </BottomNavigation>
        </Box>
      )}
    </>
  );
}

ReportsBottomNavigation.propTypes = {
  /**
   * Defines selected navigation option
   */
  selected: PropTypes.string.isRequired,
  /**
   * Callback called when new option is selected
   */
  onSelected: PropTypes.func.isRequired,
};
